import { AxiosResponse } from "axios";
import { endPoints } from "utils/constants";
import { crudService } from "services/crud.service";
import { NotificationManager } from "react-notifications";
import {
  defaultMetaObj,
  deleteErrorResponse,
  initialInventoryState,
  propertyDeleteErrorModified,
} from "store/constants";
import { getErrorMessage, getPaginatedUrlWithSearchParams } from "store/helper";
import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";

const { individualPropertyEndPoint } = endPoints;

export const fetchAllIndividualProperty = createAsyncThunk(
  "inventory/fetchAllIndividualProperty",
  async (
    {
      page,
      limit,
      searchParamsObj = {},
    }: { page: number; limit?: number; searchParamsObj?: {} },
    { rejectWithValue }
  ) => {
    const paginatedUrl = getPaginatedUrlWithSearchParams({
      pageNumber: page,
      limit,
      endpoint: individualPropertyEndPoint,
      searchParams: {
        ...searchParamsObj,
      },
    });

    try {
      const response: AxiosResponse = await crudService.getAll(paginatedUrl);

      return response.data;
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteIndividualProperty = createAsyncThunk(
  "inventory/deleteIndividualProperty",
  async ({ inventoryId }: any, { rejectWithValue }) => {
    try {
      const response: any = await crudService.delete(
        individualPropertyEndPoint,
        inventoryId
      );

      if (response.status === 204) {
        NotificationManager.success("Inventory Deleted Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(
        errorMessage?.includes(deleteErrorResponse)
          ? propertyDeleteErrorModified
          : errorMessage
      );
      return rejectWithValue(error.message);
    }
  }
);

export const addNewIndividualInventory = createAsyncThunk(
  "inventory/addNewIndividualInventory",
  async (newInventory: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.create(
        `${individualPropertyEndPoint}/`,
        newInventory
      );

      if (response.status === 201) {
        NotificationManager.success("Inventory Created Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

export const updateIndividualInventory = createAsyncThunk(
  "inventory/updateIndividualInventory",
  async ({ updatedInventory, inventoryId }: any, { rejectWithValue }) => {
    try {
      const response: AxiosResponse = await crudService.update(
        individualPropertyEndPoint,
        inventoryId,
        updatedInventory
      );
      if (response.status === 204) {
        NotificationManager.success("Inventory updated Successfully.");
        return response.data;
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);

      NotificationManager.error(errorMessage);
      return rejectWithValue(error.message);
    }
  }
);

const individualInventorySlice = createSlice({
  name: "inventory",
  initialState: initialInventoryState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch all inventory
    builder
      .addCase(fetchAllIndividualProperty.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        fetchAllIndividualProperty.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.inventory = action.payload?.objects as any[];
          state.meta = action.payload?.meta;
        }
      )
      .addCase(
        fetchAllIndividualProperty.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
          state.inventory = [];
          state.meta = defaultMetaObj;
        }
      );

    // delete inventory
    builder
      .addCase(deleteIndividualProperty.pending, (State) => {
        State.loading = true;
      })
      .addCase(
        deleteIndividualProperty.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        deleteIndividualProperty.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );

    // add new inventory
    builder
      .addCase(addNewIndividualInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        addNewIndividualInventory.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(addNewIndividualInventory.rejected, (state: any, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //   update inventory
    builder
      .addCase(updateIndividualInventory.pending, (state: any) => {
        state.loading = true;
      })
      .addCase(
        updateIndividualInventory.fulfilled,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
        }
      )
      .addCase(
        updateIndividualInventory.rejected,
        (state: any, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload as string;
        }
      );
  },
});

export const individualInventoryReducer = individualInventorySlice.reducer;
